import flatten from 'lodash/flatten';
import isInteger from 'lodash/isInteger';
import uniqBy from 'lodash/uniqBy';
import { ExpressionSpecification, FillLayerSpecification } from 'mapbox-gl';

import {
  ConsumerMapLegendStats,
  HcMapLayerNumberType,
  HcMapLegendStats,
  HeatmapColorTheme,
  LegendColorTable,
  LegendInterval,
  LngLatObject,
  MapLegendMetricStats,
  PropertyTypeEnum,
  ReactMapGlLngLatPair,
  TilePropertyTypes,
  VectilesMetricColorType,
  VectilesMetricGroups,
  VectilesMetricIds,
  VectilesMetrics,
  VectilesMetricsConsumer,
  VectilesMetricsHcs,
  VectilesMetricsHcsRental,
} from '@hcs/types';
import { formatNumberAbbrev, NULL_VALUE } from '@hcs/utils';

import {
  DEFAULT_LAYER_METRIC,
  HALFTONE_IMAGES,
  HEATMAP_PALETTE_MAPPING_COMEHOME,
  HEATMAP_PALETTE_MAPPING_HCS,
  PROPERTY_TYPE_TO_VECTILES_TYPE,
  SCHOOL_MARKER_IMAGE_IDS,
  TRANSPARENT_COLOR,
  VECTILES_METRICS_CONFIG,
  VECTILES_METRICS_GROUP_CONFIG,
} from '../constants';

const NUM_DIGITS_FOR_PERCENTAGE = 1;
const ONE_HUNDRED_PERCENT = 100;

/**
 * Count number of decimals places in number
 */
const countDecimals = (value: number): number => {
  return isInteger(value) ? 0 : value?.toString().split('.')?.[1]?.length || 0;
};

/**
 * Get min number increment
 */
export const getMinNumberIncrement = (num: number): number => {
  const decNum = countDecimals(num);
  if (decNum === 0) return 1;
  let incNum = '0.';
  for (let i = 0; i < decNum - 1; i++) {
    incNum += '0';
  }
  return parseFloat(incNum + '1');
};

/**
 * Create an object with color, label from Intervals object to be used for labels, tooltip in the map legend
 * @param  [object]
 * @param  {object} labelFormatterOptions
 * @return {object}
 */
export const getColorTableFromIntervals = ({
  intervals,
  numberType,
  mainMenuValue,
}: {
  intervals: LegendInterval[];
  numberType: HcMapLayerNumberType;
  mainMenuValue: VectilesMetricGroups;
}): LegendColorTable => {
  /* Allow overriding but fallback to our current assumption */

  const prefix = numberType === HcMapLayerNumberType.MONEY ? '$' : '';
  const suffix = numberType === HcMapLayerNumberType.PERCENTAGE ? '%' : '';
  return intervals.map((interval, idx) => {
    let low;
    let high;

    if (numberType !== HcMapLayerNumberType.PERCENTAGE) {
      if (interval.start < 0) {
        interval.start = 0;
      }
      if (interval.end < 0) {
        interval.end = 0;
      }
      low = formatNumberAbbrev(interval.start) || NULL_VALUE;
      high = formatNumberAbbrev(interval.end) || NULL_VALUE;
    } else {
      const decimalPlaces =
        mainMenuValue === VectilesMetricGroups.Schools
          ? 0
          : NUM_DIGITS_FOR_PERCENTAGE;
      const start = parseFloat(
        (interval.start * ONE_HUNDRED_PERCENT).toFixed(decimalPlaces),
      );
      const end = parseFloat(
        (interval.end * ONE_HUNDRED_PERCENT).toFixed(decimalPlaces),
      );
      low =
        mainMenuValue === VectilesMetricGroups.Crime ? interval.start : start;
      high = mainMenuValue === VectilesMetricGroups.Crime ? interval.end : end;
    }
    low = `${prefix}${low}${suffix}`;
    high = `${prefix}${high}${suffix}`;
    return {
      color: interval.color,
      label:
        interval.start === interval.min
          ? `< ${high}`
          : interval.end === interval.max
            ? `>= ${low}`
            : `${low} - ${high}`,
      patternImageIcon: undefined,
    };
  });
};

/**
 * Generate CSS gradient defs for each layer group and id to be used in the legend color bar
 */
export const getLegendBreakGradients = (
  intervals: LegendInterval[],
  layerId: VectilesMetrics,
  isMonochromeOverlay: boolean,
) => {
  if (isMonochromeOverlay) {
    const patterns: string[] = HALFTONE_IMAGES.filter(
      (img) => img.name !== 'empty',
    ).map((img) => {
      return `url(${img.icon})`;
    });
    return patterns;
  }

  return (
    intervals &&
    intervals
      .map((_, i) => {
        return i > 0
          ? `linear-gradient(to right, ${intervals?.[i - 1]?.color} 0%,${
              intervals?.[i]?.color
            } 100%)`
          : null;
      })
      .slice(1)
  );
};

export const isConsumerLayerEnum = (
  layer:
    | VectilesMetricsConsumer
    | VectilesMetricsHcs
    | VectilesMetricsHcsRental,
): layer is VectilesMetricsConsumer => {
  return Object.values(VectilesMetricsConsumer).includes(
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    layer as VectilesMetricsConsumer,
  );
};

export const isConsumerStats = (
  stats: ConsumerMapLegendStats | HcMapLegendStats,
): stats is ConsumerMapLegendStats => {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const firstKey = Object.keys(stats)?.[0] as VectilesMetricsConsumer;
  return firstKey && isConsumerLayerEnum(firstKey);
};

export const getMapLegendMetricStats = (
  metric: VectilesMetrics,
  breaksMapping: ConsumerMapLegendStats | HcMapLegendStats,
  propertyType?: PropertyTypeEnum | null,
): MapLegendMetricStats | undefined => {
  const propType: TilePropertyTypes | undefined = propertyType
    ? PROPERTY_TYPE_TO_VECTILES_TYPE[propertyType]
    : undefined;

  return isConsumerStats(breaksMapping) && isConsumerLayerEnum(metric)
    ? breaksMapping?.[metric]
    : propType &&
        !isConsumerStats(breaksMapping) &&
        !isConsumerLayerEnum(metric)
      ? breaksMapping?.[`${metric}_${propType.toLowerCase()}`]
      : undefined;
};

/**
 * Given a breaks mapping API object, return an object containing legend break ranges
 * and colors for a single metric
 * @param  {string} metric - the layer metric
 * @param  {object} breaksMapping - an object containing breaks for all metrics
 * @return {object}
 */
export const getLegendBreaksForMetric = ({
  vectilesMetricId,
  breaksMapping,
  propertyType,
  heatmapTheme,
}: {
  vectilesMetricId: VectilesMetricIds;
  breaksMapping: ConsumerMapLegendStats | HcMapLegendStats;
  propertyType?: PropertyTypeEnum | null;
  heatmapTheme: HeatmapColorTheme;
}) => {
  const { vectilesMetricGroup, vectilesMetric } =
    VECTILES_METRICS_CONFIG[vectilesMetricId];
  // Use a fixed 5 bin legend in 20% increments for Schools metric
  if (vectilesMetricGroup === VectilesMetricGroups.Schools) {
    const schoolPalette =
      heatmapTheme === HeatmapColorTheme.ComeHome
        ? HEATMAP_PALETTE_MAPPING_COMEHOME[VectilesMetricColorType.Schools]
        : HEATMAP_PALETTE_MAPPING_HCS[VectilesMetricColorType.Schools];
    return [
      {
        start: 0,
        end: 0.2,
        color: schoolPalette[0],
        min: 0,
        max: 1,
      },
      {
        start: 0.2,
        end: 0.4,
        color: schoolPalette[1],
        min: 0,
        max: 1,
      },
      {
        start: 0.4,
        end: 0.6,
        color: schoolPalette[2],
        min: 0,
        max: 1,
      },
      {
        start: 0.6,
        end: 0.8,
        color: schoolPalette[3],
        min: 0,
        max: 1,
      },
      {
        start: 0.8,
        end: 1,
        color: schoolPalette[4],
        min: 0,
        max: 1,
      },
    ];
  }
  //First we get the breakValues from the bins
  const metricStats = getMapLegendMetricStats(
    vectilesMetric,
    breaksMapping,
    propertyType,
  );
  const stats = metricStats
    ? metricStats
    : getMapLegendMetricStats(
        DEFAULT_LAYER_METRIC,
        breaksMapping,
        propertyType,
      );

  const breakValues: number[] | undefined = stats?.bins;
  const breaks: LegendInterval[] = [];
  if (breakValues && breakValues.length) {
    for (let idx = 0; idx < breakValues.length - 1; idx++) {
      if (
        breakValues[idx] &&
        breakValues[idx + 1] &&
        stats?.min &&
        stats?.max
      ) {
        breaks.push({
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          start: breakValues[idx] as number,
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          end: breakValues[idx + 1] as number,
          color:
            heatmapTheme === HeatmapColorTheme.ComeHome
              ? HEATMAP_PALETTE_MAPPING_COMEHOME[
                  VECTILES_METRICS_GROUP_CONFIG[vectilesMetricGroup].colorType
                ][idx] || TRANSPARENT_COLOR // This should never happen but if it does, falling back to transparent color prevents the entire layer from breaking
              : HEATMAP_PALETTE_MAPPING_HCS[
                  VECTILES_METRICS_GROUP_CONFIG[vectilesMetricGroup].colorType
                ][idx] || TRANSPARENT_COLOR,
          min: stats?.min,
          max: stats?.max,
        });
      }
    }
  } else {
    return undefined;
  }
  /* We're only using the lower bound of each bin to compute the Mapbox color assignment expression */
  return uniqBy(breaks, (item) => item.start);
};

export const lngLatToLatLng = (
  lngLat: ReactMapGlLngLatPair,
): ReactMapGlLngLatPair => [lngLat[1], lngLat[0]];

export const lngLatToLngLatObject = (
  value?: mapboxgl.LngLat,
): LngLatObject | undefined => {
  return value
    ? {
        lng: value.lng,
        lat: value.lat,
      }
    : undefined;
};

const SCHOOL_DEFAULT_COLOR = '#DCDCDC';

const getSchoolLegendIntervals = (
  theme: HeatmapColorTheme,
): LegendInterval[] => {
  const palette =
    theme === HeatmapColorTheme.ComeHome
      ? HEATMAP_PALETTE_MAPPING_COMEHOME[VectilesMetricColorType.Schools]
      : HEATMAP_PALETTE_MAPPING_HCS[VectilesMetricColorType.Schools];
  return [
    {
      start: -1,
      end: 20,
      color: palette[0],
      mapboxImageID: SCHOOL_MARKER_IMAGE_IDS.SCHOOL_MARKER_IMAGE_0,
    },
    {
      start: 20,
      end: 40,
      color: palette[1],
      mapboxImageID: SCHOOL_MARKER_IMAGE_IDS.SCHOOL_MARKER_IMAGE_1,
    },
    {
      start: 40,
      end: 60,
      color: palette[2],
      mapboxImageID: SCHOOL_MARKER_IMAGE_IDS.SCHOOL_MARKER_IMAGE_2,
    },
    {
      start: 60,
      end: 80,
      color: palette[3],
      mapboxImageID: SCHOOL_MARKER_IMAGE_IDS.SCHOOL_MARKER_IMAGE_3,
    },
    {
      start: 80,
      end: 100,
      color: palette[4],
      mapboxImageID: SCHOOL_MARKER_IMAGE_IDS.SCHOOL_MARKER_IMAGE_4,
    },
  ];
};

/**
 * Given a school's rank, return the color used to fill its district layer
 */
export const getSchoolDistrictFeatureColor = (
  rank: number,
  theme: HeatmapColorTheme,
): string => {
  for (const interval of getSchoolLegendIntervals(theme)) {
    if (rank > interval.start && rank <= interval.end) {
      return (
        interval.color ||
        SCHOOL_DEFAULT_COLOR /* this fallback should never be hit */
      );
    }
  }
  return SCHOOL_DEFAULT_COLOR;
};

/**
 * Generate a Mapbox GL expression for the halftone fill definition
 */
export const getBlocksLayerHalftoneDefinition = (
  intervals: LegendInterval[],
  layerId: VectilesMetrics,
  propType?: string,
): ExpressionSpecification => {
  /* If no breaks are available for the given metric, apply transparent color to features */
  const attribute = isConsumerLayerEnum(layerId)
    ? layerId
    : `${layerId}_${propType?.toLowerCase()}`;
  const value = ['number', ['get', attribute], -Infinity];
  return [
    'case',
    ...flatten(
      intervals.map((item, index) => {
        let condition: ExpressionSpecification = [
          'all',
          ['>=', value, item.start],
          ['<=', value, item.end],
        ];
        if (index === intervals.length - 1) {
          condition = ['>=', value, item.start];
        }
        return [condition, `halftone-shade-${index}`];
      }),
    ),
    'halftone-shade-empty',
  ];
};

/**
 * Generate a Mapbox GL expression for the color fill definition
 */
export const getBlocksLayerFillInterpolateDefinition = (
  intervals: LegendInterval[],
): (string | number)[] => {
  /* If no breaks are available for the given metric, apply transparent color to features */
  return flatten([
    /* -Infinity is set as the fallback value, rendering a transparent fill color */
    -Infinity,
    TRANSPARENT_COLOR,
    ...intervals.map((breakDef) => [breakDef.start, breakDef.color]),
  ]);
};

/**
 * Generate a Mapbox GL expression for step color fill definition
 */
export const getBlocksLayerFillStepDefinition = (
  intervals: LegendInterval[],
): (string | number)[] => {
  /* If no breaks are available for the given metric, apply transparent color to features */
  return flatten([
    TRANSPARENT_COLOR,
    ...intervals.map((breakDef) => [breakDef.start, breakDef.color]),
  ]);
};

/**
 * Get the complete fill definition for a layer
 */
export const getBlocksLayerFillDefinition = (
  hasMonochromeOverlay: boolean,
  propertyType?: PropertyTypeEnum | null,
  intervals?: LegendInterval[],
  activeLayerId?: VectilesMetrics | null,
): FillLayerSpecification['paint'] => {
  const propType = propertyType
    ? PROPERTY_TYPE_TO_VECTILES_TYPE[propertyType]
    : undefined;
  if (intervals && activeLayerId) {
    if (hasMonochromeOverlay) {
      return {
        'fill-pattern': getBlocksLayerHalftoneDefinition(
          intervals,
          activeLayerId,
          propType,
        ),
      };
    } else {
      const attribute = isConsumerLayerEnum(activeLayerId)
        ? activeLayerId
        : `${activeLayerId}_${propType?.toLowerCase()}`;
      return {
        'fill-color': [
          'case',
          ['==', ['get', attribute], null],
          TRANSPARENT_COLOR, // handle null values explicitly
          [
            'step',
            ['to-number', ['get', attribute]], // convert to number without fallback
            intervals[0]?.color || TRANSPARENT_COLOR, // default color for values below first breakpoint
            ...flatten(
              intervals.map((breakDef) => [breakDef.start, breakDef.color]),
            ),
          ],
        ],
      };
    }
  } else {
    return {
      'fill-color': TRANSPARENT_COLOR,
    };
  }
};

/**
 * Reformat the legend breaks passed via props to prepare for display in the UI
 */
export const getLegendColorTableForLayer = (
  vectilesMetricGroup: VectilesMetricGroups,
  intervals: LegendInterval[],
): LegendColorTable => {
  const vectilesMetricGroupConfig =
    VECTILES_METRICS_GROUP_CONFIG[vectilesMetricGroup];
  // Average price is currency
  const colorTable = getColorTableFromIntervals({
    intervals,
    numberType: vectilesMetricGroupConfig.numberType,
    mainMenuValue: vectilesMetricGroup,
  });

  colorTable.forEach((row, i) => {
    row.patternImageIcon = HALFTONE_IMAGES?.[i]?.icon.toString();
  });
  return colorTable;
};
